import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileXls, Clock, CalendarBlank } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoryPage = () => {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [yearlyData, setYearlyData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/reports/history`, { withCredentials: true });
      setYears(response.data);
      if (response.data.length > 0) {
        setSelectedYear(response.data[0].year);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  const fetchYearlyReport = useCallback(async () => {
    if (!selectedYear) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/reports/yearly?year=${selectedYear}`, { withCredentials: true });
      setYearlyData(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  }, [selectedYear, API_URL]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    fetchYearlyReport();
  }, [fetchYearlyReport]);

  const handleExportMonthlyExcel = async (monthStr) => {
    const [year, month] = monthStr.split('-');
    try {
      const url = `${API_URL}/api/reports/monthly/excel?year=${year}&month=${parseInt(month)}`;
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `rapport_${monthStr}.xlsx`;
      link.click();
      
      toast.success('Export Excel téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const months = {
    '01': 'Janvier', '02': 'Février', '03': 'Mars', '04': 'Avril',
    '05': 'Mai', '06': 'Juin', '07': 'Juillet', '08': 'Août',
    '09': 'Septembre', '10': 'Octobre', '11': 'Novembre', '12': 'Décembre'
  };

  const groupedByUser = yearlyData.reduce((acc, item) => {
    if (!acc[item.user_id]) {
      acc[item.user_id] = {
        name: item.user_name,
        months: {}
      };
    }
    acc[item.user_id].months[item.month] = item;
    return acc;
  }, {});

  const uniqueMonths = [...new Set(yearlyData.map(item => item.month))].sort();

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="history-page">
      <div className="page-header">
        <h1 className="page-title">Historique</h1>
        <p className="page-subtitle">Récapitulatifs par mois et par année</p>
      </div>

      {years.length > 0 ? (
        <>
          <div className="card" style={{ marginBottom: '24px' }}>
            <div className="card-header">
              <h3 className="card-title">Résumé par année</h3>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Année</th>
                  <th>Total heures</th>
                  <th>Nombre d'entrées</th>
                </tr>
              </thead>
              <tbody>
                {years.map((year) => (
                  <tr 
                    key={year.year} 
                    onClick={() => setSelectedYear(year.year)}
                    style={{ cursor: 'pointer', backgroundColor: selectedYear === year.year ? '#F4F5F7' : 'transparent' }}
                    data-testid={`year-row-${year.year}`}
                  >
                    <td style={{ fontWeight: '600' }}>{year.year}</td>
                    <td>{year.total_hours}h</td>
                    <td>{year.entries_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedYear && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Détails {selectedYear}</h3>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger data-testid="year-select" style={{ width: '120px' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y.year} value={y.year}>
                        {y.year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {yearlyData.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Mois</th>
                        <th>Total heures</th>
                        <th>Entrées</th>
                        <th>Export</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uniqueMonths.map((monthStr) => {
                        const monthKey = monthStr.split('-')[1];
                        const monthData = yearlyData.filter(d => d.month === monthStr);
                        const totalHours = monthData.reduce((sum, d) => sum + d.total_hours, 0);
                        const totalEntries = monthData.reduce((sum, d) => sum + d.entries_count, 0);

                        return (
                          <tr key={monthStr} data-testid={`month-row-${monthStr}`}>
                            <td style={{ fontWeight: '600' }}>{months[monthKey]} {monthStr.split('-')[0]}</td>
                            <td>{Math.round(totalHours * 100) / 100}h</td>
                            <td>{totalEntries}</td>
                            <td>
                              <button
                                data-testid={`export-month-${monthStr}`}
                                className="btn btn-primary btn-sm"
                                onClick={() => handleExportMonthlyExcel(monthStr)}
                              >
                                <FileXls size={16} />
                                Excel
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <CalendarBlank size={48} className="empty-state-icon" />
                  <p className="empty-state-text">Aucune donnée pour {selectedYear}</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <Clock size={48} className="empty-state-icon" />
          <p className="empty-state-text">Aucun historique disponible</p>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
